<template>
  <SkeletonItem
    v-if="firstLoading"
    class="articles-list-header articles-list-header_skeleton"
  />
  <div
    v-else
    class="articles-list-header"
  >
    {{ header }}
  </div>

  <MosaicListView
    class="articles-mosaic-list"
    :items="articles"
    :more-button="hasMoreArticles"
    more-button-text="Показать ещё"
    more-data-test="destination-articles-btn-read-more"
    :loading="loading"
    no-last-item-margin
    :number-of-skeleton-cards="2"
    @load-more="fetchData"
  >
    <template #default="{ item, mode, position }">
      <AppLink
        class="article-card-wrapper"
        :to="`/journal/articles/${item.slug}/`"
      >
        <Card
          v-article-view-tracker="getAnalyticsParams(item, position)"
          v-article-click-tracker="getAnalyticsParams(item, position)"
          :item="item"
          :mode="mode"
          :locale="ru"
        />
      </AppLink>
    </template>
    <template #skeleton="{ mode }">
      <Skeleton
        :mode="mode"
        :loading="loading"
      />
    </template>
  </MosaicListView>
</template>

<script lang="ts" setup>
  import { type JournalContentItem, Card, Skeleton } from '@experience/mosaic-list-article-card-package';
  import { MosaicListView } from '@experience/mosaic-list-view-package';
  import { SkeletonItem } from '@experience/core-skeleton-lib';
  import { ru } from 'date-fns/locale';
  import { storeToRefs } from 'pinia';
  import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue';

  import { useClickTracker, useViewTracker } from '@/services/analytics';
  import { useJournalContent } from '@/entities/journalContent';
  import type { JournalAuthorArticleClickParams, JournalAuthorArticleViewParams } from '@/shared/api/sogu';

  import { pluralize } from '@/shared/utils/language';

  interface Props {
    authorId: number;
  }

  const props = defineProps<Props>();

  const store = useJournalContent();
  const { getItemsList, reset } = store;
  const { articles, loading, hasNext: hasMoreArticles, totalArticlesCount } = storeToRefs(store);

  const firstLoading = computed(() => loading.value && !articles.value.length);

  const pageSize = ref(10);

  const header = computed(
    () => `${totalArticlesCount.value} ${pluralize(totalArticlesCount.value, ['статья', 'статьи', 'статей'])}`,
  );

  const vArticleViewTracker = useViewTracker('journal-author:article.view');
  const vArticleClickTracker = useClickTracker('journal-author:article.click');

  const getAnalyticsParams = (
    item: JournalContentItem,
    position: number,
  ): JournalAuthorArticleClickParams['params'] | JournalAuthorArticleViewParams['params'] => ({
    article_id: item.id,
    block: 'recent',
    position,
  });

  const fetchData = async () => getItemsList({ authorId: props.authorId }, { page_size: pageSize.value });

  onBeforeMount(async () => {
    if (!articles.value.length) {
      await fetchData();
    }
  });

  onBeforeUnmount(() => reset());
</script>

<style lang="scss" scoped>
  .articles-list-header {
    &_skeleton {
      width: 250px;
      height: calc(var(--alias-space-40) + var(--alias-space-4));
      border-radius: var(--alias-radius-16);
    }

    @include base-900-850-extra;

    @include tablet {
      &_skeleton {
        height: calc(var(--alias-space-24) + var(--alias-space-4));
        border-radius: var(--alias-radius-12);
      }

      @include base-700-500-extra;
    }
  }

  .articles-mosaic-list {
    margin-top: var(--alias-space-32);

    @include tablet {
      margin-top: var(--alias-space-16);
    }
  }

  .article-card-wrapper {
    color: var(--alias-color-text-default);
    text-decoration: none;

    &:focus,
    &:visited {
      color: var(--alias-color-text-default);
      text-decoration: none;
    }
  }
</style>
